import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { FreightManagerService } from 'src/app/services/freight-manager/freight-manager.service';
import { FreightManagerModalService } from 'src/app/services/freight-manager-modal/freight-manager-modal.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { PortService } from 'src/app/services/port/port.service';
import { ShippingLineService } from 'src/app/services/shipping-line/shipping-line.service';
import { RegionService } from 'src/app/services/region/region.service';
import { BookingAgentService } from 'src/app/services/booking-agent/booking-agent.service';
import { BookingAgentModalService } from 'src/app/services/booking-agent-modal/booking-agent-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-freight-manager-new',
  templateUrl: './add-edit-freight-manager-new.component.html',
  styleUrls: ['./add-edit-freight-manager-new.component.scss']
})
export class AddEditFreightManagerNewComponent extends DestructorComponent implements OnInit {

  @Input() selectedType: string = 'roro';
  @Output() freightManagerAdded = new EventEmitter<void>();
  @Output() freightManagerUpdated = new EventEmitter<void>();

  freightManagerForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedFreightManager: any = null;

  // Dropdown data
  countries: any[] = [];
  ports: any[] = [];
  shippers: any[] = [];
  regions: any[] = [];
  bookingAgents: any[] = [];

  // Loading states
  isLoadingCountries = false;
  isLoadingPorts = false;
  isLoadingShippers = false;
  isLoadingRegions = false;
  isLoadingBookingAgents = false;

  constructor(
    private fb: FormBuilder,
    private _freightManagerService: FreightManagerService,
    private _freightManagerModalService: FreightManagerModalService,
    private _globalStateService: GlobalStateService,
    private _portService: PortService,
    private _shippingLineService: ShippingLineService,
    private _regionService: RegionService,
    private _bookingAgentService: BookingAgentService,
    private _bookingAgentModalService: BookingAgentModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.loadCountries();
    this.loadShippers();
    this.loadRegions();
    this.loadBookingAgents();
  }

  private initializeForm(): void {
    this.freightManagerForm = this.fb.group({
      from_country_id: ['', [Validators.required]],
      to_country_id: ['', [Validators.required]],
      to_port_id: ['', [Validators.required]],
      shipper_id: ['', [Validators.required]],
      region_id: ['', [Validators.required]],
      booking_agent_id: [''],
      memo: [''],
      type: [this.selectedType, [Validators.required]]
    });

    // Watch for to_country_id changes to load ports
    this.freightManagerForm.get('to_country_id')?.valueChanges.subscribe(countryCode => {
      if (countryCode) {
        this.loadPorts(countryCode);
        // Reset port selection when country changes
        this.freightManagerForm.patchValue({ to_port_id: '' });
      } else {
        this.ports = [];
      }
    });
  }

  // Getter methods for form controls
  get from_country_id() { return this.freightManagerForm.get('from_country_id'); }
  get to_country_id() { return this.freightManagerForm.get('to_country_id'); }
  get to_port_id() { return this.freightManagerForm.get('to_port_id'); }
  get shipper_id() { return this.freightManagerForm.get('shipper_id'); }
  get region_id() { return this.freightManagerForm.get('region_id'); }
  get booking_agent_id() { return this.freightManagerForm.get('booking_agent_id'); }
  get memo() { return this.freightManagerForm.get('memo'); }
  get type() { return this.freightManagerForm.get('type'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_freight_manager_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceFreightManager = this._freightManagerModalService.getSelectedFreightManager();
        if (serviceFreightManager) {
          this.isEditMode = true;
          this.selectedFreightManager = serviceFreightManager;
          this.populateForm(this.selectedFreightManager);
        } else {
          this.isEditMode = false;
          this.selectedFreightManager = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._freightManagerModalService.clearSelectedFreightManager();
        this.resetForm();
      });
    }
  }

  private populateForm(freightManager: any): void {
    this.freightManagerForm.patchValue({
      from_country_id: freightManager.from_country_id?.code || freightManager.from_country?.code || '',
      to_country_id: freightManager.to_country_id?.code || freightManager.to_country?.code || '',
      to_port_id: freightManager.to_port_id?._id || freightManager.to_port?._id || '',
      shipper_id: freightManager.shipper_id?._id || freightManager.shipper?._id || '',
      region_id: freightManager.region_id?._id || freightManager.region?._id || '',
      booking_agent_id: freightManager.booking_agent_id?._id || freightManager.booking_agent?._id || '',
      memo: freightManager.memo || '',
      type: freightManager.type || this.selectedType
    });

    // Load ports for the selected to_country_id
    const countryCode = freightManager.to_country_id?.code || freightManager.to_country?.code;
    if (countryCode) {
      this.loadPorts(countryCode);
    }
  }

  private resetForm(): void {
    this.freightManagerForm.reset();
    this.freightManagerForm.patchValue({
      type: this.selectedType
    });
    this.ports = [];
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_freight_manager_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // Data loading methods
  private loadCountries(): void {
    this.isLoadingCountries = true;
    this._globalStateService.GetCountries().subscribe({
      next: (countries) => {
        this.countries = countries || [];
        this.isLoadingCountries = false;
      },
      error: (error) => {
        console.error('Error loading countries:', error);
        this.countries = [];
        this.isLoadingCountries = false;
      }
    });
  }

  private loadPorts(countryCode: string): void {
    if (!countryCode) {
      this.ports = [];
      return;
    }

    this.isLoadingPorts = true;
    this._portService.GetData('1', '', { paginate: false, country_code: countryCode }).subscribe({
      next: (response) => {
        if (response.status) {
          this.ports = response.data || [];
        } else {
          this.ports = [];
        }
        this.isLoadingPorts = false;
      },
      error: (error) => {
        console.error('Error loading ports:', error);
        this.ports = [];
        this.isLoadingPorts = false;
      }
    });
  }

  private loadShippers(): void {
    this.isLoadingShippers = true;
    this._shippingLineService.GetData('1', '', { paginate: false }).subscribe({
      next: (response) => {
        if (response.status) {
          this.shippers = response.data || [];
        } else {
          this.shippers = [];
        }
        this.isLoadingShippers = false;
      },
      error: (error) => {
        console.error('Error loading shippers:', error);
        this.shippers = [];
        this.isLoadingShippers = false;
      }
    });
  }

  private loadRegions(): void {
    this.isLoadingRegions = true;
    this._regionService.GetData('1', '', { paginate: false }).subscribe({
      next: (response) => {
        if (response.status) {
          this.regions = response.data || [];
        } else {
          this.regions = [];
        }
        this.isLoadingRegions = false;
      },
      error: (error) => {
        console.error('Error loading regions:', error);
        this.regions = [];
        this.isLoadingRegions = false;
      }
    });
  }

  private loadBookingAgents(): void {
    this.isLoadingBookingAgents = true;
    this._bookingAgentService.GetData('1', '', { paginate: false }).subscribe({
      next: (response) => {
        if (response.status) {
          this.bookingAgents = response.data || [];
        } else {
          this.bookingAgents = [];
        }
        this.isLoadingBookingAgents = false;
      },
      error: (error) => {
        console.error('Error loading booking agents:', error);
        this.bookingAgents = [];
        this.isLoadingBookingAgents = false;
      }
    });
  }

  openAddBookingAgentModal(): void {
    this._bookingAgentModalService.clearSelectedBookingAgent();
    const modalElement = document.getElementById('kt_modal_booking_agent_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onBookingAgentAdded(): void {
    this.loadBookingAgents();
  }

  onSubmit(): void {
    if (this.freightManagerForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.freightManagerForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._freightManagerService.UpdateData(this.selectedFreightManager._id, submitData)
        : this._freightManagerService.AddData(submitData);

      apiCall.subscribe({
        next: (response) => {
          if (response.status) {
            const message = this.isEditMode ? 'Freight manager updated successfully!' : 'Freight manager added successfully!';
            Swal.fire('Success!', message, 'success');

            if (this.isEditMode) {
              this.freightManagerUpdated.emit();
            } else {
              this.freightManagerAdded.emit();
            }

            this.closeModal();
          } else {
            Swal.fire('Error!', response.message || 'Failed to save freight manager.', 'error');
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error saving freight manager:', error);
          Swal.fire('Error!', 'An error occurred while saving the freight manager.', 'error');
          this.isLoading = false;
        }
      });
    } else {
      // Show validation errors
      Object.keys(this.freightManagerForm.controls).forEach(key => {
        const control = this.freightManagerForm.get(key);
        if (control && control.invalid) {
          control.markAsTouched();
        }
      });

      Swal.fire('Validation Error!', 'Please fill in all required fields correctly.', 'error');
    }
  }
}
